import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";
import { clearDirectoryCache } from "./directory";
import { resolveStudentStatusQueryParam } from "../utils/listActiveFilter";
import type { StudentTrainingCohort } from "./trainingCohorts";

export type StudentStatus = "active" | "passive" | "graduated";

export type StudentLessonProgress = {
  reserved: number;
  attendance_logged: number;
  attended: number;
  no_show: number;
};

export type Student = {
  id: string;
  tenant_id: string;
  branch_id: string;
  training_subgroup_id: string | null;
  training_cohort: StudentTrainingCohort | null;
  full_name: string;
  phone: string | null;
  email: string | null;
  identity_no: string | null;
  license_class: string | null;
  theoretical_exam_date: string | null;
  theoretical_exam_time: string | null;
  theoretical_exam_location: string | null;
  driving_exam_date: string | null;
  driving_exam_time: string | null;
  /** @deprecated driving_exam_date ile ayni (geriye uyumluluk) */
  exam_date: string | null;
  notes: string | null;
  status: StudentStatus;
  exam_ready: boolean | null;
  lesson_progress?: StudentLessonProgress;
  created_at: string;
  updated_at: string;
};

export type StudentListResponse = {
  items: Student[];
  total: number;
  limit: number;
  offset: number;
};

export type StudentInput = {
  branchId?: string;
  trainingSubgroupId?: string | null;
  fullName: string;
  phone?: string | null;
  email?: string | null;
  identityNo?: string | null;
  licenseClass?: string | null;
  examDate?: string | null;
  notes?: string | null;
  status?: StudentStatus;
};

export type StudentListSortField =
  | "full_name"
  | "training_period"
  | "training_group"
  | "training_subgroup"
  | "phone"
  | "license_class"
  | "exam_date"
  | "driving_exam_date"
  | "theoretical_exam_date"
  | "status";

export type StudentListSortDir = "asc" | "desc";

export type StudentListFilter = {
  q?: string;
  status?: StudentStatus | "";
  branchId?: string;
  trainingPeriodId?: string;
  trainingGroupId?: string;
  trainingSubgroupId?: string;
  cohortUnassigned?: boolean;
  sort?: StudentListSortField;
  order?: StudentListSortDir;
  limit?: number;
  offset?: number;
};

export type StudentListState = {
  data: StudentListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useStudentList(filter: StudentListFilter): StudentListState {
  const [data, setData] = useState<StudentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const {
    q,
    status,
    branchId,
    trainingPeriodId,
    trainingGroupId,
    trainingSubgroupId,
    cohortUnassigned,
    sort,
    order,
    limit,
    offset,
  } = filter;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<StudentListResponse>(
        "/students",
        {
          q: q || undefined,
          status: resolveStudentStatusQueryParam(status),
          branch_id: branchId || undefined,
          training_period_id: cohortUnassigned ? undefined : trainingPeriodId || undefined,
          training_group_id: cohortUnassigned ? undefined : trainingGroupId || undefined,
          training_subgroup_id: cohortUnassigned ? undefined : trainingSubgroupId || undefined,
          cohort_unassigned: cohortUnassigned ? "true" : undefined,
          sort: sort || undefined,
          order: order || undefined,
          limit: limit ?? 50,
          offset: offset ?? 0,
        },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Kursiyerler Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    q,
    status,
    branchId,
    trainingPeriodId,
    trainingGroupId,
    trainingSubgroupId,
    cohortUnassigned,
    sort,
    order,
    limit,
    offset,
    reloadKey,
  ]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, reload };
}

export async function createStudent(input: StudentInput): Promise<Student> {
  const result = await api.post<Student>("/students", input);
  clearDirectoryCache();
  return result;
}

export async function updateStudent(id: string, input: StudentInput): Promise<Student> {
  const result = await api.put<Student>(`/students/${id}`, input);
  clearDirectoryCache();
  return result;
}

export async function setStudentStatus(id: string, status: StudentStatus): Promise<Student> {
  const result = await api.post<Student>(`/students/${id}/status`, { status });
  clearDirectoryCache();
  return result;
}

export type BulkSetStudentStatusResult = {
  updated_count: number;
  skipped_count: number;
  status: StudentStatus;
  items: Student[];
};

export async function bulkSetStudentStatus(
  ids: string[],
  status: StudentStatus,
): Promise<BulkSetStudentStatusResult> {
  const result = await api.post<BulkSetStudentStatusResult>("/students/bulk-status", {
    ids,
    status,
  });
  clearDirectoryCache();
  return result;
}

export type ImportRowReport =
  | {
      row_number: number;
      status: "valid";
      preview: { fullName: string; identityNo?: string | null };
    }
  | {
      row_number: number;
      status: "error";
      error: { code: string; message: string; field?: string };
    };

export type ImportSummary = {
  dry_run: boolean;
  total_rows: number;
  valid_count: number;
  error_count: number;
  inserted_count: number;
  cohorts_to_create: CohortToCreateSummary[];
  cohorts_created_count: number;
  rows: ImportRowReport[];
};

export type CohortToCreateSummary = {
  key: string;
  period_label: string;
  group_label: string;
  subgroup_label: string;
  will_create_period: boolean;
  will_create_group: boolean;
  will_create_subgroup: boolean;
};

export async function downloadStudentTemplate(): Promise<void> {
  const blob = await api.getBlob("/students/import/template");
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "kursiyer-import-şablonu.xlsx";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function importStudentsXlsx(
  file: File,
  options: { dryRun?: boolean; branchId?: string } = {},
): Promise<ImportSummary> {
  const form = new FormData();
  form.append("file", file);
  const result = await api.upload<ImportSummary>("/students/import", form, {
    query: {
      dry_run: options.dryRun ? "true" : "false",
      branch_id: options.branchId,
    },
  });
  if (!options.dryRun && result.inserted_count > 0) {
    clearDirectoryCache();
  }
  return result;
}

export type ExamImportRowStatus = "new" | "existing" | "updated_only" | "not_found" | "error";

export type ExamImportRow = {
  row_number: number;
  status: ExamImportRowStatus;
  identity_no: string;
  full_name_pdf: string;
  full_name_system: string | null;
  student_id: string | null;
  exam_date: string;
  exam_time: string;
  exam_location: string | null;
  exam_location_address: string | null;
  phone: string | null;
  message_ready: boolean;
  error?: string;
};

export type ExamImportSummary = {
  dry_run: boolean;
  period_key: string;
  document_code: string;
  total_parsed: number;
  matched_count: number;
  new_count: number;
  existing_count: number;
  not_found_count: number;
  error_count: number;
  rows: ExamImportRow[];
};

export async function importTheoreticalExamPdf(
  file: File,
  options: { dryRun?: boolean; periodKey?: string } = {},
): Promise<ExamImportSummary> {
  const form = new FormData();
  form.append("file", file);
  return api.upload<ExamImportSummary>("/students/exam-import/theoretical-pdf", form, {
    query: {
      dry_run: options.dryRun ? "true" : "false",
      period_key: options.periodKey,
    },
  });
}

function formatExamDateTr(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}.${m}.${y}`;
}

function formatExamTimeHm(time: string): string {
  return time.slice(0, 5);
}

export function buildTheoreticalExamWhatsAppMessage(row: ExamImportRow): string {
  const name = row.full_name_system || row.full_name_pdf;
  const date = formatExamDateTr(row.exam_date);
  const time = formatExamTimeHm(row.exam_time);
  const salon = row.exam_location || "belirtilecektir";
  const address = row.exam_location_address || "";
  const lines = [
    `Sayın ${name}, teorik e-sınav giriş belgeniz oluşturulmuştur.`,
    `Tarih: ${date}`,
    `Saat: ${time}`,
    `Salon: ${salon}`,
  ];
  if (address) lines.push(`Adres: ${address}`);
  lines.push("Lütfen sınav günü geçerli kimlik belgeniz ve giriş belgesi ile 15 dakika önce hazır olunuz.");
  return lines.join("\n");
}

export type StudentLessonStats = {
  total_written: number;
  reserved_lesson_count: number;
  attendance_logged_count: number;
  attended: number;
  no_show: number;
  report_pending: number;
  cancelled: number;
  planned_upcoming: number;
  attendance_rate: number | null;
  report_completion_rate: number | null;
};

export type StudentLessonLessonRow = {
  appointment_id: string;
  starts_at: string;
  ends_at: string;
  status: string;
  lesson_type: string;
  instructor_name: string;
  vehicle_plate: string;
  has_report: boolean;
  attended: boolean | null;
};

export type StudentLessonStatsResponse = {
  student: Student;
  range: { start_date: string | null; end_date: string | null };
  stats: StudentLessonStats;
  lessons: StudentLessonLessonRow[];
};

export async function fetchStudentLessonStats(
  studentId: string,
  range: { startDate?: string; endDate?: string } = {},
  options?: { signal?: AbortSignal },
): Promise<StudentLessonStatsResponse> {
  return api.get<StudentLessonStatsResponse>(
    `/students/${studentId}/lesson-stats`,
    {
      start_date: range.startDate,
      end_date: range.endDate,
    },
    { signal: options?.signal },
  );
}

export function buildWhatsAppUrl(phone: string | null | undefined, message?: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D+/g, "");
  if (digits.length === 0) return null;
  let normalized = digits;
  if (normalized.startsWith("00")) normalized = normalized.slice(2);
  if (normalized.startsWith("0")) normalized = `90${normalized.slice(1)}`;
  if (!normalized.startsWith("90") && normalized.length === 10) normalized = `90${normalized}`;
  const base = `https://wa.me/${normalized}`;
  if (!message?.trim()) return base;
  return `${base}?text=${encodeURIComponent(message.trim())}`;
}
